import React from "react";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import { useOrgs } from "@/context/OrgsContext";
import CreatableCombobox from "@/components/admin/CreatableCombobox";

export const orgLookup = (orgs) => ({
  association: id => orgs.associations.find(a => a.id === id)?.name,
  school: id => orgs.schools.find(s => s.id === id)?.name,
});

export function scopeLabel(item, orgs) {
  const l = orgLookup(orgs);
  return l.school(item.school_id) || l.association(item.association_id) || "";
}

// Association + School pickers from the shared dynamic list; Super Admin can type to create new entries.
export function AssociationSchoolFields({ value, onChange, schoolLabel = "School", schoolNoneLabel, hideSchool, testidPrefix }) {
  const { can } = useAuth();
  const { orgs, createAssociation, createSchool } = useOrgs();
  const canCreate = can("manage_organizations");
  const schools = orgs.schools.filter(s => s.association_id === value.association_id);
  return (
    <div className={`grid grid-cols-1 ${hideSchool ? "" : "md:grid-cols-2"} gap-3`}>
      <div>
        <Label className="text-xs">Association *</Label>
        <CreatableCombobox value={value.association_id} options={orgs.associations} placeholder="Select association"
          onChange={id => onChange({ association_id: id, school_id: "" })}
          onCreate={canCreate ? createAssociation : null} testid={`${testidPrefix}-association`}/>
      </div>
      {!hideSchool && (
        <div>
          <Label className="text-xs">{schoolLabel}</Label>
          <CreatableCombobox value={value.school_id} options={schools} disabled={!value.association_id}
            placeholder={value.association_id ? "Select school" : "Pick an association first"} noneLabel={schoolNoneLabel}
            onChange={id => onChange({ ...value, school_id: id })}
            onCreate={canCreate ? (name => createSchool(name, value.association_id)) : null} testid={`${testidPrefix}-school`}/>
        </div>
      )}
    </div>
  );
}

// Role-dependent profile assignment used when creating / editing users.
export function UserAssignmentFields({ role, value, onChange, sports, testidPrefix = "scope" }) {
  const needsSchool = role === "school_admin" || role === "coach";
  const toggleSport = (id, on) => {
    const cur = value.sport_ids || [];
    onChange({ ...value, sport_ids: on ? [...cur, id] : cur.filter(x => x !== id) });
  };
  return (
    <div className="space-y-3">
      <AssociationSchoolFields value={value} onChange={v => onChange({ ...value, ...v })} testidPrefix={testidPrefix}
        hideSchool={role === "association_admin"} schoolLabel={needsSchool ? "School *" : "School"}
        schoolNoneLabel={needsSchool ? undefined : "— None —"}/>
      {role === "coach" && (
        <div data-testid={`${testidPrefix}-sports`}>
          <Label className="text-xs">Assigned sports *</Label>
          <div className="grid grid-cols-2 gap-1 mt-1 max-h-40 overflow-auto border border-slate-200 p-2">
            {sports.map(s => (
              <label key={s.id} className="flex items-center gap-2 text-sm">
                <Checkbox checked={(value.sport_ids || []).includes(s.id)} onCheckedChange={v => toggleSport(s.id, !!v)}
                  data-testid={`${testidPrefix}-sport-${s.slug || s.id}`}/>{s.name}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Record tagging: Super Admin picks Association/School; everyone else inherits from their profile.
export function RecordTagFields({ user, value, onChange, testidPrefix = "owner" }) {
  const { orgs } = useOrgs();
  if (user.role !== "super_admin") {
    const l = orgLookup(orgs);
    return (
      <p className="text-xs text-slate-600 bg-slate-50 border border-slate-200 px-3 py-2" data-testid={`${testidPrefix}-inherited`}>
        Records will be tagged to <strong>{l.association(user.association_id) || "your association"}</strong>
        {user.school_id && <> / <strong>{l.school(user.school_id)}</strong></>} from your profile.
      </p>
    );
  }
  return <AssociationSchoolFields value={value} onChange={onChange} schoolNoneLabel="— Association-wide —" testidPrefix={testidPrefix}/>;
}
